import React, { useEffect, useState, useMemo } from "react";
import API from "../../services/api";
import UserSidebar from "../../components/UserSidebar";
import { 
  FiSearch, FiMapPin, FiImage, FiPlayCircle, 
  FiMic, FiX, FiPhone, 
  FiClipboard, FiRefreshCw, FiExternalLink, FiStar,
  FiUserCheck, FiGrid, FiList, FiAlignJustify
} from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";

const UserTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("All");

  // View Mode: "table" | "grid" | "list"
  const [viewMode, setViewMode] = useState("table");

  // Date Range Filters: fromDate, toDate
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Media preview modal
  const [previewMedia, setPreviewMedia] = useState(null); // { type: 'image'|'video'|'audio', url: string }

  // Rating modal states
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [ratingTicket, setRatingTicket] = useState(null);
  const [ratingStars, setRatingStars] = useState(5);
  const [ratingFeedback, setRatingFeedback] = useState("");
  const [submittingRating, setSubmittingRating] = useState(false);

  const MEDIA_BASE_URL = "https://api.ajplservices.tech/uploads/tickets/";
  const PROFILE_BASE_URL = "https://api.ajplservices.tech/uploads/profile/";

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const res = await API.get("/tickets/my-tickets");
      if (res.data.success) {
        setTickets(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching user tickets:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredTickets = useMemo(() => {
    let list = tickets;

    // 1. Status Filter
    if (activeTab !== "All") {
      list = list.filter(t => (t.status || "").toLowerCase() === activeTab.toLowerCase());
    }

    // 2. Search Text
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      list = list.filter(t => 
        (t.ticket_code && t.ticket_code.toLowerCase().includes(s)) ||
        (t.category_name && t.category_name.toLowerCase().includes(s)) ||
        (t.subcategory_name && t.subcategory_name.toLowerCase().includes(s)) ||
        (t.description && t.description.toLowerCase().includes(s)) ||
        (t.hod_name && t.hod_name.toLowerCase().includes(s)) ||
        (t.worker_name && t.worker_name.toLowerCase().includes(s))
      );
    }

    // 3. Date Range Filter
    if (fromDate) {
      const from = new Date(fromDate);
      from.setHours(0, 0, 0, 0);
      list = list.filter(t => new Date(t.created_at) >= from);
    }
    if (toDate) {
      const to = new Date(toDate);
      to.setHours(23, 59, 59, 999);
      list = list.filter(t => new Date(t.created_at) <= to);
    }

    return list;
  }, [tickets, activeTab, searchTerm, fromDate, toDate]);

  // Status counts
  const counts = useMemo(() => {
    const res = { All: tickets.length, Pending: 0, "Work in Progress": 0, Resolved: 0, Rejected: 0 };
    tickets.forEach(t => {
      const st = (t.status || "").toLowerCase();
      if (st === "pending") res.Pending++;
      else if (st === "work in progress" || st === "in queue" || st === "next week") res["Work in Progress"]++;
      else if (st === "resolved") res.Resolved++;
      else if (st === "rejected") res.Rejected++;
    });
    return res;
  }, [tickets]);

  const getStatusBadge = (status) => {
    const s = (status || "").toLowerCase().trim();
    if (s === "pending") {
      return { bg: "#FFFBEB", color: "#D97706", border: "#FDE68A", label: "Pending" };
    }
    if (s === "in queue") {
      return { bg: "#FFF7ED", color: "#EA580C", border: "#FFEDD5", label: "In Queue" };
    }
    if (s === "work in progress") {
      return { bg: "#EFF6FF", color: "#2563EB", border: "#BFDBFE", label: "Work In Progress" };
    }
    if (s === "next week" || s === "next month" || s === "next quarter") {
      return { bg: "#F5F3FF", color: "#7C3AED", border: "#DDD6FE", label: status };
    }
    if (s === "hold") {
      return { bg: "#FAF5FF", color: "#8B5CF6", border: "#E9D5FF", label: "On Hold" };
    }
    if (s === "resolved") {
      return { bg: "#ECFDF5", color: "#059669", border: "#A7F3D0", label: "Resolved" };
    }
    if (s === "rejected") {
      return { bg: "#FEF2F2", color: "#DC2626", border: "#FECACA", label: "Rejected" };
    }
    return { bg: "#F8FAFC", color: "#64748B", border: "#E2E8F0", label: status || "Open" };
  };

  // WhatsApp helper
  const openWhatsApp = (phone, ticketCode) => {
    if (!phone) {
      alert("Contact number not available.");
      return;
    }
    const cleanPhone = phone.replace(/[^0-9]/g, "");
    const text = encodeURIComponent(`Regarding my Ticket ID: ${ticketCode}`);
    window.open(`https://wa.me/${cleanPhone}?text=${text}`, "_blank");
  };

  const handleOpenRating = (ticket) => {
    setRatingTicket(ticket);
    setRatingStars(5);
    setRatingFeedback("");
    setShowRatingModal(true);
  };

  const submitRating = async () => {
    if (!ratingTicket) return;
    setSubmittingRating(true);
    try {
      const res = await API.post("/tickets/submit-rating", {
        ticketId: ratingTicket.id,
        ticket_id: ratingTicket.id,
        rating: ratingStars,
        feedback: ratingFeedback.trim()
      });
      if (res.data.success) {
        alert("Thank you for your rating! ⭐");
        setShowRatingModal(false);
        fetchTickets();
        if (selectedTicket && selectedTicket.id === ratingTicket.id) {
          setSelectedTicket({ ...selectedTicket, rating_status: 1, rating: ratingStars, feedback: ratingFeedback });
        }
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to submit rating");
    } finally {
      setSubmittingRating(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFromDate("");
    setToDate("");
    setActiveTab("All");
  };

  return (
    <div style={s.layout}>
      <UserSidebar />

      <div style={s.main}>
        {/* TOP HEADER */}
        <div style={s.pageHeader}>
          <div>
            <div style={s.breadcrumb}>SERVICE PORTAL &bull; TICKET AUDIT</div>
            <h1 style={s.pageTitle}>My Raised Issues & Status</h1>
            <p style={s.pageSubtitle}>Track real-time progress, assigned personnel, direct WhatsApp/Call, and resolution details.</p>
          </div>

          <div style={s.headerRight}>
            <button style={s.refreshBtn} onClick={fetchTickets} title="Refresh records">
              <FiRefreshCw className={loading ? "spin" : ""} size={16} />
            </button>
            <div style={s.searchWrap}>
              <FiSearch style={s.searchIcon} size={15} />
              <input 
                style={s.searchInput}
                placeholder="Search ticket, category, HOD..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button style={s.clearBtn} onClick={() => setSearchTerm("")}>
                  <FiX size={13} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* CONTROLS ROW: STATUS TABS + DATE RANGE FILTER + VIEW SWITCHER */}
        <div style={s.controlsRow}>
          {/* METRIC PILLS / TABS */}
          <div style={s.tabsWrap}>
            {["All", "Pending", "Work in Progress", "Resolved", "Rejected"].map(tab => (
              <button
                key={tab}
                style={{
                  ...s.tabBtn,
                  ...(activeTab === tab ? s.tabBtnActive : {})
                }}
                onClick={() => setActiveTab(tab)}
              >
                <span>{tab === "Work in Progress" ? "In Progress" : tab}</span>
                <span style={{
                  ...s.tabCount,
                  background: activeTab === tab ? "#1B2559" : "#E2E8F0",
                  color: activeTab === tab ? "#FFF" : "#64748B"
                }}>
                  {counts[tab] || 0}
                </span>
              </button>
            ))}
          </div>

          {/* RIGHT SIDE: DATE RANGE + VIEW TOGGLE */}
          <div style={s.dateAndViewWrap}>
            {/* DATE RANGE FILTER */}
            <div style={s.dateFilterWrap}>
              <div style={s.dateInputGroup}>
                <span style={s.dateLabel}>From:</span>
                <input 
                  type="date"
                  value={fromDate}
                  onChange={e => setFromDate(e.target.value)}
                  style={s.dateInput}
                />
              </div>

              <div style={s.dateInputGroup}>
                <span style={s.dateLabel}>To:</span>
                <input 
                  type="date"
                  value={toDate}
                  onChange={e => setToDate(e.target.value)}
                  style={s.dateInput}
                />
              </div>

              {(fromDate || toDate) && (
                <button 
                  onClick={() => { setFromDate(""); setToDate(""); }}
                  style={s.clearDateBtn}
                  title="Clear Date Filter"
                >
                  <FiX size={12} /> Clear
                </button>
              )}
            </div>

            {/* VIEW MODE TOGGLE (Table, Grid, List) */}
            <div style={s.viewToggleContainer}>
              <button 
                onClick={() => setViewMode("table")}
                style={{ ...s.viewToggleBtn, ...(viewMode === "table" ? s.viewToggleBtnActive : {}) }}
                title="Table View"
              >
                <FiAlignJustify size={14} />
                <span>Table</span>
              </button>
              <button 
                onClick={() => setViewMode("grid")}
                style={{ ...s.viewToggleBtn, ...(viewMode === "grid" ? s.viewToggleBtnActive : {}) }}
                title="Grid View"
              >
                <FiGrid size={14} />
                <span>Grid</span>
              </button>
              <button 
                onClick={() => setViewMode("list")}
                style={{ ...s.viewToggleBtn, ...(viewMode === "list" ? s.viewToggleBtnActive : {}) }}
                title="List View"
              >
                <FiList size={14} />
                <span>List</span>
              </button>
            </div>
          </div>
        </div>

        {/* ACTIVE FILTER SUMMARY (if dates or search applied) */}
        {(fromDate || toDate || searchTerm || activeTab !== "All") && (
          <div style={s.activeFilterBar}>
            <span style={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>Active Filters:</span>
            {activeTab !== "All" && <span style={s.filterTag}>Status: {activeTab}</span>}
            {searchTerm && <span style={s.filterTag}>Search: "{searchTerm}"</span>}
            {fromDate && <span style={s.filterTag}>From: {fromDate}</span>}
            {toDate && <span style={s.filterTag}>To: {toDate}</span>}
            <span style={{ fontSize: 12, color: "#1B2559", fontWeight: 800 }}>({filteredTickets.length} matching)</span>
            <button onClick={clearFilters} style={s.resetFilterLink}>Reset all</button>
          </div>
        )}

        {/* MAIN CONTENT AREA */}
        <div style={s.contentArea}>
          {/* TICKET CONTAINER (TABLE / GRID / LIST) */}
          <div style={{ ...s.tableCard, flex: selectedTicket ? "1 1 58%" : "1 1 100%" }}>
            {loading ? (
              <div style={s.centerBox}>
                <div style={s.spinner}></div>
                <div style={s.loadingText}>Syncing your tickets...</div>
              </div>
            ) : filteredTickets.length === 0 ? (
              <div style={s.emptyBox}>
                <FiClipboard size={44} color="#CBD5E1" />
                <h3 style={s.emptyTitle}>No tickets found</h3>
                <p style={s.emptySubtitle}>No tickets match the selected filters or date range.</p>
                {(fromDate || toDate || searchTerm || activeTab !== "All") && (
                  <button onClick={clearFilters} style={s.emptyResetBtn}>Clear all filters</button>
                )}
              </div>
            ) : viewMode === "table" ? (
              /* ── 1. TABLE VIEW ── */
              <div style={s.tableContainer}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>Ticket Code</th>
                      <th style={s.th}>Department & Subcategory</th>
                      <th style={s.th}>Issue Details</th>
                      <th style={s.th}>Location</th>
                      <th style={s.th}>Concern Person / Field Worker</th>
                      <th style={s.th}>Status</th>
                      <th style={s.th}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTickets.map(t => {
                      const badge = getStatusBadge(t.status);
                      const isSelected = selectedTicket && selectedTicket.id === t.id;
                      return (
                        <tr 
                          key={t.id} 
                          style={{
                            ...s.tr,
                            background: isSelected ? "#F4F7FE" : "transparent"
                          }}
                          onClick={() => setSelectedTicket(t)}
                        >
                          <td style={s.td}>
                            <div style={s.codeBadge}>{t.ticket_code}</div>
                            <div style={s.dateSub}>
                              {new Date(t.created_at).toLocaleDateString("en-IN", {
                                day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit"
                              })}
                            </div>
                          </td>
                          <td style={s.td}>
                            <div style={s.catName}>{t.category_name || "General"}</div>
                            <div style={s.subCatName}>{t.subcategory_name || "Support"}</div>
                          </td>
                          <td style={s.td}>
                            <div style={s.descText}>{t.description}</div>
                            {/* Media indicators */}
                            <div style={s.mediaPills}>
                              {t.image && <span style={s.mediaPill}><FiImage size={10}/> Photo</span>}
                              {t.video && <span style={s.mediaPill}><FiPlayCircle size={10}/> Video</span>}
                              {t.audio && <span style={s.mediaPill}><FiMic size={10}/> Audio</span>}
                            </div>
                          </td>
                          <td style={s.td}>
                            <div style={s.locBadge}>
                              <FiMapPin size={11} color="#D4AF37" />
                              <span>{t.scanned_location || "Direct Entry"}</span>
                            </div>
                          </td>
                          <td style={s.td}>
                            <div style={s.hodInfo}>
                              <div style={s.hodName}>{t.hod_name || "Reviewing..."}</div>
                              {t.worker_name && (
                                <div style={{ fontSize: 11, color: "#2563EB", fontWeight: 700, display: "flex", alignItems: "center", gap: 3 }}>
                                  <FiUserCheck size={11} /> {t.worker_name}
                                </div>
                              )}
                            </div>
                          </td>
                          <td style={s.td}>
                            <span style={{
                              ...s.statusBadge,
                              background: badge.bg,
                              color: badge.color,
                              border: `1px solid ${badge.border}`
                            }}>
                              <span style={{ ...s.statusDot, background: badge.color }}></span>
                              {badge.label}
                            </span>
                          </td>
                          <td style={s.td}>
                            <button style={s.viewDetailsBtn} onClick={(e) => { e.stopPropagation(); setSelectedTicket(t); }}>
                              <span>Details</span>
                              <FiExternalLink size={12} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : viewMode === "grid" ? (
              /* ── 2. GRID VIEW ── */
              <div style={s.cardsGrid}>
                {filteredTickets.map(t => {
                  const badge = getStatusBadge(t.status);
                  const isSelected = selectedTicket && selectedTicket.id === t.id;
                  return (
                    <div 
                      key={t.id}
                      onClick={() => setSelectedTicket(t)}
                      style={{
                        ...s.gridCard,
                        border: isSelected ? "2px solid #D4AF37" : "1.5px solid #F0F2FA",
                        background: isSelected ? "#FFFDF5" : "#FFF"
                      }}
                    >
                      <div style={s.gridCardHeader}>
                        <div>
                          <div style={s.codeBadge}>{t.ticket_code}</div>
                          <div style={s.dateSub}>
                            {new Date(t.created_at).toLocaleDateString("en-IN", {
                              day: "2-digit", month: "short", year: "numeric"
                            })}
                          </div>
                        </div>

                        <span style={{
                          ...s.statusBadge,
                          background: badge.bg,
                          color: badge.color,
                          border: `1px solid ${badge.border}`
                        }}>
                          <span style={{ ...s.statusDot, background: badge.color }}></span>
                          {badge.label}
                        </span>
                      </div>

                      <div style={{ margin: "14px 0" }}>
                        <div style={s.catName}>{t.category_name || "General"} &bull; {t.subcategory_name || "Request"}</div>
                        <p style={s.gridCardDesc}>{t.description}</p>
                      </div>

                      {/* Location & Media indicator */}
                      <div style={s.gridCardMeta}>
                        <div style={s.locBadge}>
                          <FiMapPin size={11} color="#D4AF37" />
                          <span>{t.scanned_location || "Direct Entry"}</span>
                        </div>
                        <div style={s.mediaPills}>
                          {t.image && <span style={s.mediaPill}><FiImage size={10}/> Photo</span>}
                          {t.video && <span style={s.mediaPill}><FiPlayCircle size={10}/> Video</span>}
                          {t.audio && <span style={s.mediaPill}><FiMic size={10}/> Audio</span>}
                        </div>
                      </div>

                      {/* Footer: HOD and inspect button */}
                      <div style={s.gridCardFooter}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: "#A3AED0", textTransform: "uppercase" }}>Concern Person</div>
                          <div style={{ fontSize: 12, fontWeight: 800, color: "#1B2559", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {t.hod_name || "Reviewing..."}
                          </div>
                        </div>
                        <button style={s.viewDetailsBtn} onClick={(e) => { e.stopPropagation(); setSelectedTicket(t); }}>
                          <span>Inspect</span>
                          <FiExternalLink size={12} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* ── 3. LIST VIEW ── */
              <div style={s.listContainer}>
                {filteredTickets.map(t => {
                  const badge = getStatusBadge(t.status);
                  const isSelected = selectedTicket && selectedTicket.id === t.id;
                  return (
                    <div 
                      key={t.id}
                      onClick={() => setSelectedTicket(t)}
                      style={{
                        ...s.listItem,
                        border: isSelected ? "1.5px solid #D4AF37" : "1px solid #F0F2FA",
                        background: isSelected ? "#FFFDF5" : "#FFF"
                      }}
                    >
                      <div style={{ minWidth: 120 }}>
                        <div style={s.codeBadge}>{t.ticket_code}</div>
                        <div style={s.dateSub}>
                          {new Date(t.created_at).toLocaleDateString("en-IN", {
                            day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit"
                          })}
                        </div>
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                          <span style={s.catName}>{t.category_name} &bull; {t.subcategory_name}</span>
                          <div style={s.locBadge}>
                            <FiMapPin size={10} color="#D4AF37" />
                            <span>{t.scanned_location || "Direct"}</span>
                          </div>
                        </div>
                        <div style={s.listDescText}>{t.description}</div>
                      </div>

                      <div style={{ minWidth: 140 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "#A3AED0", textTransform: "uppercase" }}>Assigned HOD</div>
                        <div style={{ fontSize: 12, fontWeight: 800, color: "#1B2559" }}>{t.hod_name || "Reviewing..."}</div>
                        {t.worker_name && (
                          <div style={{ fontSize: 11, color: "#2563EB", fontWeight: 700, display: "flex", alignItems: "center", gap: 3 }}>
                            <FiUserCheck size={11} /> {t.worker_name}
                          </div>
                        )}
                      </div>

                      <div>
                        <span style={{
                          ...s.statusBadge,
                          background: badge.bg,
                          color: badge.color,
                          border: `1px solid ${badge.border}`
                        }}>
                          <span style={{ ...s.statusDot, background: badge.color }}></span>
                          {badge.label}
                        </span>
                      </div>

                      <button style={s.viewDetailsBtn} onClick={(e) => { e.stopPropagation(); setSelectedTicket(t); }}>
                        <span>Inspect</span>
                        <FiExternalLink size={12} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* INSPECTION DRAWER (MOBILE-MATCHED FULL DETAILS) */}
          {selectedTicket && (
            <div style={s.drawer}>
              {/* DRAWER HEADER */}
              <div style={s.drawerHeader}>
                <div style={s.drawerHeaderTop}>
                  <div>
                    <div style={{ fontSize: 11, color: "#D4AF37", fontWeight: 800, letterSpacing: 0.8, textTransform: "uppercase" }}>Ticket Details</div>
                    <div style={s.drawerTicketCode}>{selectedTicket.ticket_code}</div>
                  </div>
                  <button style={s.drawerClose} onClick={() => setSelectedTicket(null)} title="Close Panel">
                    <FiX size={15} />
                  </button>
                </div>
                <div style={s.drawerDate}>
                  Raised on {new Date(selectedTicket.created_at).toLocaleString("en-IN", {
                    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
                  })}
                </div>
              </div>

              <div style={s.drawerBody}>
                {/* 1. STATUS BADGE CARD */}
                {(() => {
                  const badge = getStatusBadge(selectedTicket.status);
                  return (
                    <div style={{
                      ...s.drawerStatusCard,
                      background: badge.bg,
                      border: `1px solid ${badge.border}`
                    }}>
                      <div style={{ ...s.statusDotLarge, background: badge.color }}></div>
                      <div style={{ flex: 1 }}>
                        <div style={{ ...s.drawerStatusTitle, color: badge.color }}>{badge.label}</div>
                        <div style={s.drawerStatusSub}>
                          {selectedTicket.status === "Resolved" 
                            ? "Issue has been resolved and verified by service personnel."
                            : selectedTicket.status === "Work in Progress"
                            ? "Personnel is actively assigned and working on this request."
                            : selectedTicket.status === "In Queue"
                            ? "Scheduled in service queue for execution."
                            : "Ticket is in review with department leadership."}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* 2. PROBLEM DETAILS SECTION */}
                <div style={s.drawerSection}>
                  <div style={s.sectionHeader}>PROBLEM DETAILS</div>
                  <div style={s.infoCard}>
                    <div style={s.infoRow}>
                      <span style={s.infoLabel}>Issue Category:</span>
                      <span style={s.infoVal}>{selectedTicket.category_name || "General"}</span>
                    </div>
                    <div style={s.infoRow}>
                      <span style={s.infoLabel}>Sub-Category:</span>
                      <span style={s.infoVal}>{selectedTicket.subcategory_name || "General"}</span>
                    </div>
                    {selectedTicket.subcategory_desc && (
                      <div style={s.infoRow}>
                        <span style={s.infoLabel}>Sub-Category Info:</span>
                        <span style={{ ...s.infoVal, color: "#64748B", fontWeight: 500, fontSize: 11 }}>
                          {selectedTicket.subcategory_desc}
                        </span>
                      </div>
                    )}
                    <div style={s.infoRow}>
                      <span style={s.infoLabel}>Scanned Location:</span>
                      <span style={{ ...s.infoVal, color: "#DC2626", fontWeight: 800 }}>
                        <FiMapPin size={11} style={{ marginRight: 4 }} />
                        {selectedTicket.scanned_location || "Direct Entry"}
                      </span>
                    </div>

                    <div style={s.descBox}>
                      <div style={s.descBoxLabel}>Issue Description:</div>
                      <div style={s.descBoxContent}>{selectedTicket.description}</div>
                    </div>
                  </div>
                </div>

                {/* 3. ASSIGNMENT & DIRECT CONTACT (HOD & WORKER WITH CALL + WHATSAPP) */}
                <div style={s.drawerSection}>
                  <div style={s.sectionHeader}>ASSIGNMENT & CONTACT</div>
                  <div style={s.contactCard}>
                    {/* HOD / Department Head */}
                    <div style={s.personRow}>
                      <div style={s.personAvatar}>
                        {selectedTicket.hod_image ? (
                          <img 
                            src={`${PROFILE_BASE_URL}${selectedTicket.hod_image}`} 
                            alt="HOD" 
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <span style={s.avatarInitial}>
                            {(selectedTicket.hod_name || "H")[0].toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={s.personRole}>Department Head (HOD)</div>
                        <div style={s.personName}>{selectedTicket.hod_name || "Reviewing..."}</div>
                        <div style={s.personPhoneText}>{selectedTicket.hod_phone || "Contact not listed"}</div>
                      </div>

                      {selectedTicket.hod_phone && selectedTicket.hod_phone !== "No Phone" && (
                        <div style={s.commGroup}>
                          <a 
                            href={`tel:${selectedTicket.hod_phone}`}
                            style={{ ...s.commBtn, background: "#ECFDF5", color: "#10B981", border: "1px solid #A7F3D0" }}
                            title="Call HOD"
                          >
                            <FiPhone size={15} />
                          </a>
                          <button
                            type="button"
                            onClick={() => openWhatsApp(selectedTicket.hod_phone, selectedTicket.ticket_code)}
                            style={{ ...s.commBtn, background: "#F0FDF4", color: "#25D366", border: "1px solid #BBF7D0" }}
                            title="WhatsApp HOD"
                          >
                            <FaWhatsapp size={17} />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Assigned Worker */}
                    <div style={{ ...s.personRow, borderTop: "1px dashed #E2E8F0", paddingTop: 14, marginTop: 14 }}>
                      <div style={{ ...s.personAvatar, background: "#EFF6FF", borderColor: "#BFDBFE" }}>
                        <FiUserCheck size={18} color="#2563EB" />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={s.personRole}>Assigned Field Worker</div>
                        <div style={{ ...s.personName, color: selectedTicket.worker_name ? "#2563EB" : "#94A3B8" }}>
                          {selectedTicket.worker_name || "Pending Assignment"}
                        </div>
                        <div style={s.personPhoneText}>
                          {selectedTicket.worker_phone || (selectedTicket.worker_name ? "Direct worker" : "Will be allocated soon")}
                        </div>
                      </div>

                      {selectedTicket.worker_phone && selectedTicket.worker_phone !== "No Phone" && (
                        <div style={s.commGroup}>
                          <a 
                            href={`tel:${selectedTicket.worker_phone}`}
                            style={{ ...s.commBtn, background: "#ECFDF5", color: "#10B981", border: "1px solid #A7F3D0" }}
                            title="Call Field Worker"
                          >
                            <FiPhone size={15} />
                          </a>
                          <button
                            type="button"
                            onClick={() => openWhatsApp(selectedTicket.worker_phone, selectedTicket.ticket_code)}
                            style={{ ...s.commBtn, background: "#F0FDF4", color: "#25D366", border: "1px solid #BBF7D0" }}
                            title="WhatsApp Field Worker"
                          >
                            <FaWhatsapp size={17} />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Remarks / Review Notes */}
                    <div style={s.remarksBox}>
                      <div style={s.remarksLabel}>Resolution Remarks / Review Notes:</div>
                      <div style={s.remarksContent}>
                        {selectedTicket.remarks || "Wait for Concern Person review & operational updates."}
                      </div>
                    </div>
                  </div>
                </div>

                {/* 4. ATTACHED MEDIA EVIDENCE */}
                {(selectedTicket.image || selectedTicket.video || selectedTicket.audio) && (
                  <div style={s.drawerSection}>
                    <div style={s.sectionHeader}>MEDIA EVIDENCE</div>
                    <div style={s.mediaGrid}>
                      {selectedTicket.image && (
                        <div 
                          style={s.mediaCard} 
                          onClick={() => setPreviewMedia({ type: "image", url: `${MEDIA_BASE_URL}${selectedTicket.image}` })}
                        >
                          <img 
                            src={`${MEDIA_BASE_URL}${selectedTicket.image}`} 
                            alt="Evidence" 
                            style={s.mediaThumb}
                          />
                          <div style={s.mediaCardLabel}>
                            <FiImage size={12}/> Click to Preview Photo
                          </div>
                        </div>
                      )}

                      {selectedTicket.video && (
                        <div 
                          style={s.mediaCard}
                          onClick={() => setPreviewMedia({ type: "video", url: `${MEDIA_BASE_URL}${selectedTicket.video}` })}
                        >
                          <div style={{ ...s.mediaThumb, display: "flex", alignItems: "center", justifyContent: "center", background: "#0F172A", color: "#FFF" }}>
                            <FiPlayCircle size={36} color="#D4AF37" />
                          </div>
                          <div style={s.mediaCardLabel}>
                            <FiPlayCircle size={12}/> Click to Play Video
                          </div>
                        </div>
                      )}

                      {selectedTicket.audio && (
                        <div style={{ ...s.mediaCard, padding: "14px", gridColumn: "1 / -1" }}>
                          <div style={{ fontSize: "11px", fontWeight: "800", marginBottom: "8px", color: "#1B2559", display: "flex", alignItems: "center", gap: 6 }}>
                            <FiMic size={14} color="#D4AF37" /> Voice Recording Note:
                          </div>
                          <audio src={`${MEDIA_BASE_URL}${selectedTicket.audio}`} controls style={{ width: "100%" }} />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* 5. RATING & FEEDBACK SECTION */}
                {selectedTicket.status === "Resolved" && (
                  <div style={s.drawerSection}>
                    <div style={s.sectionHeader}>SERVICE SATISFACTION</div>
                    {selectedTicket.rating_status === 1 ? (
                      <div style={s.ratedCard}>
                        <div style={s.ratedStars}>
                          {[1, 2, 3, 4, 5].map(st => (
                            <FiStar 
                              key={st} 
                              size={18} 
                              fill={st <= (selectedTicket.rating || 0) ? "#D4AF37" : "none"} 
                              color={st <= (selectedTicket.rating || 0) ? "#D4AF37" : "#CBD5E1"} 
                            />
                          ))}
                        </div>
                        <div style={s.ratedFeedback}>
                          "{selectedTicket.feedback || "Thank you for the prompt service!"}"
                        </div>
                        <div style={s.ratedBadge}>Feedback Submitted &bull; Verified</div>
                      </div>
                    ) : (
                      <button style={s.rateNowBtn} onClick={() => handleOpenRating(selectedTicket)}>
                        <FiStar size={16} /> Rate Resolution Service ⭐
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* MEDIA PREVIEW MODAL */}
        {previewMedia && (
          <div style={s.modalOverlay} onClick={() => setPreviewMedia(null)}>
            <div style={{ ...s.modalContent, width: "min(720px, 95%)", background: "#0F172A", padding: 0 }} onClick={e => e.stopPropagation()}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                <span style={{ color: "#FFF", fontSize: 13, fontWeight: 700 }}>Media Evidence Preview</span>
                <button style={{ background: "none", border: "none", color: "#FFF", cursor: "pointer" }} onClick={() => setPreviewMedia(null)}>
                  <FiX size={18} />
                </button>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
                {previewMedia.type === "image" ? (
                  <img src={previewMedia.url} alt="Evidence" style={{ maxWidth: "100%", maxHeight: "75vh", borderRadius: 12, objectFit: "contain" }} />
                ) : (
                  <video src={previewMedia.url} controls autoPlay style={{ maxWidth: "100%", maxHeight: "75vh", borderRadius: 12 }} />
                )}
              </div>
            </div>
          </div>
        )}

        {/* FEEDBACK & RATING POPUP MODAL */}
        {showRatingModal && (
          <div style={s.modalOverlay} onClick={() => setShowRatingModal(false)}>
            <div style={s.modalContent} onClick={e => e.stopPropagation()}>
              <div style={s.modalHeader}>
                <div style={s.modalTitleWrap}>
                  <FiStar color="#D4AF37" size={20} />
                  <h3 style={s.modalTitle}>Rate Service Experience</h3>
                </div>
                <button style={s.modalClose} onClick={() => setShowRatingModal(false)}>
                  <FiX size={16} />
                </button>
              </div>

              <div style={s.modalBody}>
                <p style={s.modalSub}>
                  How satisfied are you with the resolution of ticket <strong>{ratingTicket?.ticket_code}</strong>?
                </p>

                {/* Star selectors */}
                <div style={s.starRow}>
                  {[1, 2, 3, 4, 5].map(st => (
                    <button
                      key={st}
                      type="button"
                      style={s.starBtn}
                      onClick={() => setRatingStars(st)}
                    >
                      <FiStar 
                        size={28} 
                        fill={st <= ratingStars ? "#D4AF37" : "none"} 
                        color={st <= ratingStars ? "#D4AF37" : "#CBD5E1"} 
                      />
                    </button>
                  ))}
                </div>

                <div style={s.ratingLabel}>
                  {ratingStars === 5 && "😍 Excellent Service"}
                  {ratingStars === 4 && "😊 Good Service"}
                  {ratingStars === 3 && "😐 Average"}
                  {ratingStars === 2 && "😞 Bad"}
                  {ratingStars === 1 && "😡 Very Bad"}
                </div>

                <div style={s.fieldWrap}>
                  <label style={s.fieldLabel}>Additional Feedback / Remarks (Optional):</label>
                  <textarea
                    style={s.fieldTextarea}
                    rows="3"
                    placeholder="Write a few words about worker speed, solution quality..."
                    value={ratingFeedback}
                    onChange={e => setRatingFeedback(e.target.value)}
                  />
                </div>
              </div>

              <div style={s.modalFooter}>
                <button style={s.modalCancelBtn} onClick={() => setShowRatingModal(false)}>
                  Cancel
                </button>
                <button 
                  style={s.modalSubmitBtn} 
                  disabled={submittingRating}
                  onClick={submitRating}
                >
                  {submittingRating ? "Submitting..." : "Submit Rating"}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

/* ─── STYLES (Admin & HOD Match with Date Filter and Grid/List/Table Views) ─── */
const s = {
  layout: { display: "flex", background: "#F8FAFC", minHeight: "100vh", fontFamily: "'Plus Jakarta Sans', sans-serif" },
  main: { marginLeft: 280, padding: "32px 36px 100px", flex: 1, minWidth: 0 },

  pageHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, gap: 20 },
  breadcrumb: { fontSize: 11, color: "#A3AED0", fontWeight: 700, letterSpacing: 0.8, marginBottom: 6 },
  pageTitle: { margin: 0, fontSize: 26, fontWeight: 900, color: "#1B2559", letterSpacing: "-0.5px" },
  pageSubtitle: { margin: "4px 0 0", fontSize: 13, color: "#A3AED0", fontWeight: 500 },

  headerRight: { display: "flex", gap: 10, alignItems: "center" },
  refreshBtn: { width: 42, height: 42, borderRadius: 12, border: "1px solid #E9EDF7", background: "#FFF", color: "#64748B", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "0.2s" },
  searchWrap: { position: "relative", display: "flex", alignItems: "center" },
  searchIcon: { position: "absolute", left: 14, color: "#A3AED0", pointerEvents: "none" },
  searchInput: { padding: "11px 36px 11px 38px", borderRadius: 12, border: "1px solid #E9EDF7", width: 270, outline: "none", background: "#FFF", fontSize: 13, fontFamily: "inherit", color: "#1B2559", fontWeight: 500 },
  clearBtn: { position: "absolute", right: 10, background: "none", border: "none", cursor: "pointer", color: "#A3AED0", padding: 2 },

  /* CONTROLS ROW */
  controlsRow: { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 14, marginBottom: 16 },
  tabsWrap: { display: "flex", gap: 8, overflowX: "auto", paddingBottom: 2 },
  tabBtn: { display: "flex", alignItems: "center", gap: 8, padding: "9px 15px", borderRadius: 12, border: "1px solid #E9EDF7", background: "#FFF", color: "#64748B", fontSize: 12, fontWeight: 700, cursor: "pointer", transition: "0.2s" },
  tabBtnActive: { background: "#1B2559", color: "#FFF", borderColor: "#1B2559", boxShadow: "0 4px 15px rgba(27,37,89,0.12)" },
  tabCount: { padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 800 },

  dateAndViewWrap: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" },
  dateFilterWrap: { display: "flex", alignItems: "center", gap: 8, background: "#FFF", padding: "6px 12px", borderRadius: 12, border: "1px solid #E9EDF7" },
  dateInputGroup: { display: "flex", alignItems: "center", gap: 5 },
  dateLabel: { fontSize: 11, fontWeight: 700, color: "#64748B" },
  dateInput: { border: "1px solid #E2E8F0", borderRadius: 8, padding: "4px 8px", fontSize: 11, fontFamily: "inherit", color: "#1B2559", background: "#F8FAFC", outline: "none" },
  clearDateBtn: { background: "#FEE2E2", color: "#DC2626", border: "none", borderRadius: 6, padding: "4px 8px", fontSize: 11, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 3 },

  viewToggleContainer: { display: "flex", background: "#FFF", border: "1px solid #E9EDF7", borderRadius: 12, padding: 3 },
  viewToggleBtn: { display: "flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 9, border: "none", background: "none", color: "#64748B", fontSize: 12, fontWeight: 700, cursor: "pointer", transition: "0.15s" },
  viewToggleBtnActive: { background: "#1B2559", color: "#D4AF37", boxShadow: "0 2px 8px rgba(27,37,89,0.15)" },

  activeFilterBar: { display: "flex", alignItems: "center", gap: 8, background: "#FFF", border: "1px solid #F0F2FA", padding: "8px 16px", borderRadius: 12, marginBottom: 16, flexWrap: "wrap" },
  filterTag: { background: "#EFF6FF", color: "#2563EB", padding: "3px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700 },
  resetFilterLink: { background: "none", border: "none", color: "#D4AF37", fontSize: 12, fontWeight: 800, cursor: "pointer", textDecoration: "underline", marginLeft: "auto" },

  contentArea: { display: "flex", gap: 24, alignItems: "flex-start" },
  tableCard: { background: "#FFF", borderRadius: 20, border: "1px solid #F0F2FA", boxShadow: "0 2px 14px rgba(0,0,0,0.03)", overflow: "hidden", minHeight: 450 },
  tableContainer: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", textAlign: "left" },
  th: { padding: "14px 18px", fontSize: 11, fontWeight: 800, color: "#A3AED0", textTransform: "uppercase", letterSpacing: 0.6, borderBottom: "1.5px solid #F4F7FE", background: "#FAFCFF" },
  tr: { borderBottom: "1px solid #F8FAFC", cursor: "pointer", transition: "background 0.15s" },
  td: { padding: "16px 18px", verticalAlign: "middle" },

  codeBadge: { fontSize: 13, fontWeight: 900, color: "#1B2559", letterSpacing: 0.3 },
  dateSub: { fontSize: 11, color: "#94A3B8", marginTop: 3 },
  catName: { fontSize: 13, fontWeight: 800, color: "#1B2559" },
  subCatName: { fontSize: 11, color: "#64748B", marginTop: 2 },
  descText: { fontSize: 12, color: "#475569", lineHeight: 1.5, maxHeight: 38, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", maxWidth: 220 },
  mediaPills: { display: "flex", gap: 5, marginTop: 5 },
  mediaPill: { display: "inline-flex", alignItems: "center", gap: 3, background: "#F1F5F9", padding: "2px 6px", borderRadius: 6, fontSize: 10, fontWeight: 700, color: "#475569" },

  locBadge: { display: "inline-flex", alignItems: "center", gap: 5, background: "#FFFDF5", border: "1px solid #FEF08A", padding: "4px 8px", borderRadius: 8, fontSize: 11, fontWeight: 700, color: "#854D0E" },
  hodInfo: { display: "flex", flexDirection: "column", gap: 2 },
  hodName: { fontSize: 12, fontWeight: 700, color: "#1B2559" },

  statusBadge: { display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 20, fontSize: 11, fontWeight: 800 },
  statusDot: { width: 6, height: 6, borderRadius: "50%" },
  viewDetailsBtn: { display: "inline-flex", alignItems: "center", gap: 5, padding: "7px 14px", borderRadius: 10, background: "#1B2559", border: "none", color: "#D4AF37", fontSize: 11, fontWeight: 800, cursor: "pointer", boxShadow: "0 2px 8px rgba(27,37,89,0.12)" },

  /* GRID VIEW */
  cardsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))", gap: 18, padding: 22 },
  gridCard: { borderRadius: 16, padding: "18px 20px", display: "flex", flexDirection: "column", justifyContent: "space-between", cursor: "pointer", transition: "all 0.18s" },
  gridCardHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start" },
  gridCardDesc: { fontSize: 12, color: "#64748B", margin: "6px 0 0", lineHeight: 1.5, maxHeight: 40, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" },
  gridCardMeta: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, paddingTop: 10, borderTop: "1px dashed #F0F2FA" },
  gridCardFooter: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, paddingTop: 12, borderTop: "1px solid #F0F2FA" },

  /* LIST VIEW */
  listContainer: { display: "flex", flexDirection: "column", gap: 12, padding: 20 },
  listItem: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 18, padding: "16px 20px", borderRadius: 14, cursor: "pointer", transition: "all 0.15s" },
  listDescText: { fontSize: 12, color: "#64748B", lineHeight: 1.4, maxHeight: 34, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" },

  centerBox: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "80px 20px", gap: 12 },
  spinner: { width: 36, height: 36, border: "3px solid #E2E8F0", borderTopColor: "#1B2559", borderRadius: "50%", animation: "spin 0.8s linear infinite" },
  loadingText: { fontSize: 13, color: "#A3AED0", fontWeight: 600 },
  emptyBox: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "90px 20px", textAlign: "center" },
  emptyTitle: { margin: "14px 0 4px", fontSize: 16, fontWeight: 800, color: "#1B2559" },
  emptySubtitle: { margin: 0, fontSize: 13, color: "#94A3B8" },
  emptyResetBtn: { marginTop: 14, padding: "8px 18px", borderRadius: 10, border: "1px solid #D4AF37", background: "#FFFDF5", color: "#D4AF37", fontWeight: 800, fontSize: 12, cursor: "pointer" },

  /* DRAWER (MOBILE-MATCHED DESKTOP VIEW) */
  drawer: { flex: "1 1 42%", background: "#FFF", borderRadius: 20, border: "1px solid #F0F2FA", boxShadow: "0 8px 30px rgba(27,37,89,0.06)", overflow: "hidden", position: "sticky", top: 20 },
  drawerHeader: { padding: "18px 22px", background: "#1B2559", color: "#FFF" },
  drawerHeaderTop: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  drawerTicketCode: { fontSize: 19, fontWeight: 900, letterSpacing: 0.5, color: "#FFF", marginTop: 2 },
  drawerClose: { width: 28, height: 28, borderRadius: "50%", background: "rgba(255,255,255,0.15)", border: "none", color: "#FFF", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" },
  drawerDate: { fontSize: 11, color: "rgba(255,255,255,0.7)", marginTop: 6 },

  drawerBody: { padding: "20px 22px", maxHeight: "78vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: 20 },
  drawerSection: { display: "flex", flexDirection: "column", gap: 8 },
  sectionHeader: { fontSize: 11, fontWeight: 800, color: "#A3AED0", letterSpacing: 0.8, textTransform: "uppercase" },

  drawerStatusCard: { display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", borderRadius: 14 },
  statusDotLarge: { width: 10, height: 10, borderRadius: "50%", flexShrink: 0 },
  drawerStatusTitle: { fontSize: 14, fontWeight: 800 },
  drawerStatusSub: { fontSize: 11, color: "#64748B", marginTop: 2, lineHeight: 1.4 },

  infoCard: { background: "#F8FAFC", borderRadius: 14, border: "1px solid #F0F2FA", padding: "14px 16px" },
  infoRow: { display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, padding: "6px 0", borderBottom: "1px dashed #E2E8F0" },
  infoLabel: { color: "#64748B", fontWeight: 600 },
  infoVal: { color: "#1B2559", fontWeight: 700, textAlign: "right" },

  descBox: { marginTop: 10, background: "#FFF", padding: "12px 14px", borderRadius: 12, border: "1px solid #E2E8F0" },
  descBoxLabel: { fontSize: 11, fontWeight: 800, color: "#64748B", marginBottom: 4 },
  descBoxContent: { fontSize: 12, color: "#1B2559", lineHeight: 1.6 },

  contactCard: { background: "#F8FAFC", borderRadius: 14, border: "1px solid #F0F2FA", padding: "16px" },
  personRow: { display: "flex", alignItems: "center", gap: 12 },
  personAvatar: { width: 44, height: 44, borderRadius: "50%", background: "#FFFDF5", border: "2px solid #F5E8BA", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  avatarInitial: { color: "#D4AF37", fontWeight: 800, fontSize: 18 },
  personRole: { fontSize: 10, fontWeight: 800, color: "#A3AED0", textTransform: "uppercase", letterSpacing: 0.5 },
  personName: { fontSize: 14, fontWeight: 800, color: "#1B2559", marginTop: 1 },
  personPhoneText: { fontSize: 11, color: "#64748B", marginTop: 1 },

  commGroup: { display: "flex", alignItems: "center", gap: 8 },
  commBtn: { width: 36, height: 36, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", textDecoration: "none", transition: "all 0.15s", border: "none" },

  remarksBox: { marginTop: 14, paddingTop: 12, borderTop: "1px dashed #E2E8F0" },
  remarksLabel: { fontSize: 10, fontWeight: 800, color: "#A3AED0", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 },
  remarksContent: { fontSize: 12, fontStyle: "italic", color: "#475569", lineHeight: 1.5 },

  mediaGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },
  mediaCard: { borderRadius: 12, border: "1px solid #E2E8F0", overflow: "hidden", background: "#F8FAFC", cursor: "pointer", transition: "transform 0.15s, border-color 0.15s" },
  mediaThumb: { width: "100%", height: 110, objectFit: "cover" },
  mediaCardLabel: { padding: "8px", fontSize: 11, fontWeight: 700, color: "#1B2559", display: "flex", alignItems: "center", gap: 4, justifyContent: "center", background: "#FFF" },

  ratedCard: { background: "#FFFDF5", border: "1px solid #FEF08A", borderRadius: 14, padding: "14px 16px", textAlign: "center" },
  ratedStars: { display: "flex", justifyContent: "center", gap: 4, marginBottom: 8 },
  ratedFeedback: { fontSize: 12, fontStyle: "italic", color: "#854D0E", marginBottom: 6 },
  ratedBadge: { fontSize: 10, fontWeight: 800, color: "#A16207", textTransform: "uppercase", letterSpacing: 0.5 },
  rateNowBtn: { display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "12px", borderRadius: 12, background: "#D4AF37", color: "#1B2559", border: "none", fontWeight: 800, fontSize: 13, cursor: "pointer", boxShadow: "0 4px 15px rgba(212,175,55,0.3)" },

  /* MODAL */
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 },
  modalContent: { width: "min(460px, 100%)", background: "#FFF", borderRadius: 24, boxShadow: "0 24px 60px rgba(0,0,0,0.2)", padding: "26px 28px", boxSizing: "border-box" },
  modalHeader: { padding: "4px 0 16px", borderBottom: "1px solid #F4F7FE", display: "flex", justifyContent: "space-between", alignItems: "center" },
  modalTitleWrap: { display: "flex", alignItems: "center", gap: 10 },
  modalTitle: { margin: 0, fontSize: 16, fontWeight: 900, color: "#1B2559" },
  modalClose: { background: "none", border: "none", cursor: "pointer", color: "#94A3B8" },
  modalBody: { padding: "16px 0" },
  modalSub: { margin: "0 0 16px", fontSize: 13, color: "#64748B", lineHeight: 1.5 },
  starRow: { display: "flex", justifyContent: "center", gap: 8, marginBottom: 12 },
  starBtn: { background: "none", border: "none", cursor: "pointer", padding: 4 },
  ratingLabel: { textAlign: "center", fontSize: 13, fontWeight: 800, color: "#D4AF37", marginBottom: 16 },
  fieldWrap: { display: "flex", flexDirection: "column", gap: 6 },
  fieldLabel: { fontSize: 11, fontWeight: 700, color: "#475569" },
  fieldTextarea: { padding: "10px 12px", borderRadius: 10, border: "1.5px solid #E2E8F0", outline: "none", fontSize: 12, fontFamily: "inherit" },
  modalFooter: { padding: "14px 0 0", borderTop: "1px solid #F4F7FE", display: "flex", justifyContent: "flex-end", gap: 10 },
  modalCancelBtn: { padding: "9px 16px", borderRadius: 9, border: "1px solid #E2E8F0", background: "#F8FAFC", fontWeight: 700, fontSize: 12, cursor: "pointer" },
  modalSubmitBtn: { padding: "9px 20px", borderRadius: 9, border: "none", background: "#1B2559", color: "#FFF", fontWeight: 800, fontSize: 12, cursor: "pointer" },
};

export default UserTickets;
